import { useCallback, useEffect, useMemo, useState } from 'react'
import { Modal } from 'react-bootstrap'
import Button, {
  ButtonVariant,
  ButtonSizes,
  ButtonColors,
} from 'design-system/button'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus, faCheck, faSortAlt } from '@fortawesome/pro-solid-svg-icons'
import { useTranslation } from 'react-i18next'

import styles from './Sort.module.scss'
import { AddSort } from './AddSort'
import { options as optionsBase, optionsKey } from './constants'
import { FieldOption, OptionValue } from './types'
import { DropResult } from 'react-beautiful-dnd'
import { Order } from './Order'
import {
  IFolderSortSettings,
  IFolderSortSettingsField,
} from 'models/folder.model'
import Loader, { LoaderSizes } from 'components/ui/loader/Loader'
import classNames from 'classnames'
import CommonToolTip from 'components/ui/tooltip/commonToolTip'
import { advancedOrder } from '../../enum/folder.enum'
import ConfirmationModal from 'components/ui/modals/confirmation/ConfirmationModal'
import { useTranslationRankmi } from 'helpers/translations.helper'

interface SortProps {
  show: boolean
  loading?: boolean
  onClose(): void
  onSave(fields: FieldOption[], changed: boolean): void
  sortSettings?: IFolderSortSettings

  customOrder?: boolean
  modalTitle?: string
}

export const Sort = ({
  show,
  onClose,
  onSave,
  sortSettings,
  loading,
  customOrder: custom = false,
  modalTitle,
}: SortProps) => {
  const { t } = useTranslation()
  const tRelative = useTranslationRankmi({
    prefix: 'administration.folders.folder',
  })

  const options = useMemo(
    () =>
      optionsBase
        .filter(({ value }) => (value === advancedOrder ? custom : true))
        .map(({ label, ...rest }) => ({
          label: t(`${optionsKey}.${label}`),
          ...rest,
        })),
    [custom, t]
  )

  const optionsMap = useMemo(
    () => new Map(options.map((option) => [option.value, option])),
    [options]
  )

  const mapFields = useCallback(
    (fields?: IFolderSortSettingsField[]) =>
      (fields || []).map(
        ({ attribute, direction }): FieldOption => ({
          direction,
          option: optionsMap.get(attribute),
        })
      ),
    [optionsMap]
  )

  const [fields, setFields] = useState<FieldOption[]>(() =>
    mapFields(sortSettings?.fields)
  )

  useEffect(() => {
    setFields(mapFields(sortSettings?.fields))
  }, [sortSettings, mapFields])

  const [orderFields, setOrderFields] = useState(false)
  const fieldsMap = useMemo(
    () =>
      new Map(
        fields.map<[OptionValue | undefined, FieldOption]>((field) => [
          field.option?.value,
          field,
        ])
      ),
    [fields]
  )

  const valid = useMemo(() => fields.every(({ option }) => option), [fields])

  const [indexDeleteConfirmation, setIndexDeleteConfirmation] = useState(-1)

  const deleteConfirmation = useMemo(
    () => fields[indexDeleteConfirmation],
    [fields, indexDeleteConfirmation]
  )

  const closeDeleteConfirmation = useCallback(
    () => setIndexDeleteConfirmation(-1),
    []
  )

  const showModal = useMemo(
    () => show && !deleteConfirmation,
    [show, deleteConfirmation]
  )

  const reorder = (
    list: FieldOption[],
    startIndex: number,
    endIndex: number
  ) => {
    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)
    result.splice(endIndex, 0, removed)
    return result
  }

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) {
      return
    }
    const items = reorder(fields, result.source.index, result.destination.index)
    setFields(items)
  }

  const onSelected = (
    newField: Partial<FieldOption> | null | undefined,
    index: number
  ): void => {
    const newFields = [...fields]
    if (newField) {
      newFields[index] = newField
    } else newFields.splice(index, 1)
    setFields(newFields)
  }

  return (
    <>
      <Modal
        size="lg"
        show={showModal}
        onHide={onClose}
        backdropClassName={styles.modalBackdrop}
        dialogClassName={styles.modal}
        onExited={() => {
          setFields(mapFields(sortSettings?.fields))
        }}
      >
        <Modal.Header closeButton className="ml-3 mr-3 mt-2 pb-0">
          <Modal.Title>
            <p>
              {modalTitle ||
                t('administration.folders.folder.sortConfiguration.title')}
            </p>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="position-relative">
            <div className="row">
              <div className="col">
                <div className={styles.subtitle}>
                  {t(
                    'administration.folders.folder.sortConfiguration.subtitle'
                  )}
                </div>
              </div>
              <div className="col-auto">
                <CommonToolTip
                  text={t(
                    'administration.folders.folder.sortConfiguration.reorder'
                  )}
                  position="top"
                >
                  <Button
                    size={ButtonSizes.Small}
                    color={
                      orderFields
                        ? ButtonColors.Green
                        : ButtonColors.GreyLighten
                    }
                    className="m-0"
                    disabled={!valid || fields.length < 2}
                    onClick={() => setOrderFields(!orderFields)}
                  >
                    <FontAwesomeIcon icon={orderFields ? faCheck : faSortAlt} />
                  </Button>
                </CommonToolTip>
              </div>
            </div>
            <div className="row">
              {!fields.length && (
                <div className="col-12 mt-3">
                  <AddSort
                    options={options}
                    onSelected={(newField) => {
                      newField && setFields([newField])
                    }}
                  />
                </div>
              )}
              {orderFields && <Order list={fields} onDragEnd={onDragEnd} />}

              {!orderFields &&
                fields.map((field, i) => {
                  const { option } = field
                  return (
                    <div key={i} className="col-12 mt-3">
                      <AddSort
                        field={field}
                        options={options.filter(
                          ({ value }) =>
                            option?.value === value || !fieldsMap.has(value)
                        )}
                        onSelected={(newValue) => {
                          const isAdvancedOrder =
                            field.option?.value === advancedOrder
                          if (!newValue && isAdvancedOrder)
                            setIndexDeleteConfirmation(i)
                          else onSelected(newValue, i)
                        }}
                      />
                    </div>
                  )
                })}
            </div>
            {fields.length < options.length && (
              <div className="text-center mt-3">
                <Button
                  className="w-100 m-0"
                  color={ButtonColors.Blue}
                  size={ButtonSizes.Small}
                  onClick={() => setFields([...fields, {}])}
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                  <span>{tRelative('sortConfiguration.add')}</span>
                </Button>
              </div>
            )}
            {loading && (
              <div
                className={classNames(
                  'position-absolute top-0',
                  styles.loadingRoot
                )}
              >
                <Loader sizes={LoaderSizes.ExtraSmall} color />
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="ml-3 mr-3 mt-2 border-top-0">
          <Button
            variant={ButtonVariant.Secondary}
            size={ButtonSizes.Small}
            onClick={onClose}
          >
            {t('commons.buttons.close')}
          </Button>
          <Button
            variant={ButtonVariant.Success}
            size={ButtonSizes.Small}
            disabled={!valid || loading}
            onClick={async () => {
              const originalFields = mapFields(sortSettings?.fields)
              const changed =
                originalFields.length !== fields.length ||
                originalFields.some(({ option, direction }, i) => {
                  const other = fields[i]
                  return (
                    option?.value !== other?.option?.value ||
                    direction !== other?.direction
                  )
                })

              await onSave(fields, changed)
            }}
          >
            <FontAwesomeIcon icon={faCheck} className="mr-2" />
            {t('commons.buttons.save')}
          </Button>
        </Modal.Footer>
      </Modal>
      <ConfirmationModal
        show={!!deleteConfirmation}
        backdropClassName={styles.modalBackdrop}
        title=""
        text={tRelative('sortConfiguration.deleteConfirmation')}
        size="lg"
        acceptButtonText={t('components.modal.buttons.delete')}
        onAccept={() => {
          onSelected(null, indexDeleteConfirmation)
          closeDeleteConfirmation()
        }}
        onClose={() => closeDeleteConfirmation()}
      />
    </>
  )
}
